import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import SyfDisputes, { DisputeFeature } from 'digital-disputes';
import noop from 'lodash-es/noop';
import { useEffect, useRef, useState } from 'react';
import { RenderIf } from 'syf-component-library/ui/atoms';
import Card from 'syf-component-library/ui/atoms/Card/Card';
import VerticalCardLoader from 'syf-component-library/ui/atoms/Loader/Skeleton/SlideInModalLoader';
import Inset from 'syf-component-library/ui/spacing/Inset';
import { ErrorBoundary } from 'syf-component-library/ui/templates';
import useDisputesFooterLinkFeature from '../../../hooks/use-disputes-footer-link-feature';
import { triggerAnalyticsEvent as triggerAnalyticsEventSyf } from 'syf-js-utilities';
import { getDisputesConfig } from '../../../const/activity-config';
import { activityPagePreview, activityPageView } from '../../../const/analytics';
import { useActivityContext } from '../../../context/activity-store';
import getPlatform from '../../../helpers/getPlatform';
import { createCssIdByTransactionId } from '../../../helpers/utils/activity-utils';
import useAccountFeatures from '../../../hooks/use-account-features';
import useActivityRefetch from '../../../hooks/use-activity-refetch';
import useDisputesFeature from '../../../hooks/use-disputes-feature';
import useDisputesLinkFeature from '../../../hooks/use-disputes-link-feature';
import useSelectPayEligibilityData from '../../../hooks/use-select-pay-eligibility';
import useSelectedLanguage from '../../../hooks/use-selected-language';
import { TransactionProvider } from '../../../hooks/use-transaction';
import { FeatureOptions } from '../../../typings/activity/activity-types';
import { ModalOptions } from '../../../typings/activity/modal-types';
import GenericError from '../../common/generic-error';
import CanceledErrorIllustration from '../../common/illustrations/cancel-payment/error';
import CancelPaymentComplete from '../cancel-payment/complete';
import CancelPaymentConfirm from '../cancel-payment/confirm';
import ExtraInfo from '../extra-info';
import Modal from '../modal';
import NavBar from '../navbar';
import TableContainer from '../table-container';
import { StyledMain } from './subcomponents';
var Main = function (_a) {
    var accountId = _a.accountId, accountIdType = _a.accountIdType, activity = _a.activity, config = _a.config, platform = _a.platform, handleNavigate = _a.handleNavigate, isDisplayCondensed = _a.isDisplayCondensed, isError = _a.isError, isFetching = _a.isFetching, isSuccess = _a.isSuccess, totalTransactions = _a.totalTransactions, refetchAccountActivity = _a.refetchAccountActivity, showLoadMore = _a.showLoadMore, _b = _a.triggerAnalyticsEvent, triggerAnalyticsEvent = _b === void 0 ? triggerAnalyticsEventSyf : _b;
    var _c = useState(), activeTransactionId = _c[0], setActiveTransactionId = _c[1];
    var _d = useState(), currentTransaction = _d[0], setCurrentTransaction = _d[1];
    var _e = useState(FeatureOptions.activity), activeTab = _e[0], setActiveTab = _e[1];
    var _f = useState(false), isExtraInfoOpen = _f[0], setIsExtraInfoOpen = _f[1];
    var _g = useState(false), showQuestionnaire = _g[0], setShowQuestionnaire = _g[1];
    var _h = useState(ModalOptions.default), currentModal = _h[0], setCurrentModal = _h[1];
    var setInitialData = useActivityContext().setInitialData;
    var _j = useActivityRefetch({
        currentModal: currentModal,
        currentTransaction: currentTransaction,
        refetchAccountActivity: refetchAccountActivity,
        setCurrentModal: setCurrentModal
    }), cancelPaymentFetching = _j.cancelPaymentFetching, shouldCancelPaymentRender = _j.shouldCancelPaymentRender;
    var selectedPlatform = getPlatform(platform);
    var accountFeatures = useAccountFeatures({ selectedPlatform: selectedPlatform });
    var eligibleTransactions = useSelectPayEligibilityData({
        accountFeatures: accountFeatures,
        accountId: accountId,
        accountIdType: accountIdType,
        config: config
    }).eligibleTransactions;
    var disputesEnabled = useDisputesFeature({ accountFeatures: accountFeatures });
    var disputesLinkEnabled = useDisputesLinkFeature({ accountFeatures: accountFeatures });
    var disputesFooterLinkEnabled = useDisputesFooterLinkFeature({
        accountFeatures: accountFeatures
    });
    var tableRef = useRef(null);
    useSelectedLanguage();
    useEffect(function () {
        setInitialData({
            accountId: accountId,
            accountIdType: accountIdType,
            activity: activity,
            isDisplayCondensed: isDisplayCondensed,
            config: config,
            isError: isError,
            isFetching: isFetching,
            isSuccess: isSuccess,
            refetchAccountActivity: refetchAccountActivity,
            showLoadMore: showLoadMore,
            totalTransactions: totalTransactions,
            triggerAnalyticsEvent: triggerAnalyticsEvent
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activity, isError, isFetching, isSuccess, showLoadMore]);
    useEffect(function () {
        if (!tableRef.current)
            return;
        // Makes the content behind the modal inaccessible when open
        tableRef.current.inert = isExtraInfoOpen;
    }, [isExtraInfoOpen]);
    useEffect(function () {
        var sendAnalyticEvent = function () {
            var values = isDisplayCondensed
                ? activityPagePreview
                : activityPageView;
            if (activeTab === 'activity') {
                triggerAnalyticsEvent(values);
            }
        };
        if (document.readyState === 'complete') {
            sendAnalyticEvent();
        }
        else {
            window.addEventListener('load', sendAnalyticEvent, false);
            // Remove the event listener when component unmounts
            return function () { return window.removeEventListener('load', sendAnalyticEvent); };
        }
        return undefined;
    }, [isDisplayCondensed, activeTab, triggerAnalyticsEvent]);
    var handleTransactionDetails = function (index) {
        if (index !== activeTransactionId) {
            setIsExtraInfoOpen(true);
            return;
        }
        if (index === activeTransactionId && isExtraInfoOpen) {
            setIsExtraInfoOpen(false);
            return;
        }
        if (index === activeTransactionId && !isExtraInfoOpen) {
            setIsExtraInfoOpen(true);
        }
    };
    var handleSelectActivity = function (id) {
        var transactionById = activity.items[id];
        setCurrentTransaction(transactionById);
        setActiveTransactionId(id);
        handleTransactionDetails(id);
    };
    /**
     * @description Adds focus after slight delay back to active transaction row after modal closes
     */
    var focusBackTransaction = function () {
        if (!tableRef.current)
            return;
        setTimeout(function () {
            var _a;
            var cssId = createCssIdByTransactionId(activeTransactionId);
            var elem = (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("#".concat(cssId));
            if (!(elem === null || elem === void 0 ? void 0 : elem.focus)) {
                // Element with transaction ID ${cssId} wasn't found
                return;
            }
            elem.focus();
        }, 500);
    };
    var handleCloseModal = function () {
        setActiveTransactionId('');
        setIsExtraInfoOpen(false);
        setShowQuestionnaire(false);
        setCurrentModal(ModalOptions.default);
        focusBackTransaction();
    };
    var handleTab = function (tab) {
        setActiveTab(tab);
    };
    return (_jsxs(StyledMain, { condensed: isDisplayCondensed, children: [_jsx(Card, { children: _jsxs("div", { ref: tableRef, children: [_jsx(RenderIf, { condition: !isDisplayCondensed, children: _jsx(NavBar, { handleTab: handleTab, tab: activeTab, disputesLinkEnabled: disputesLinkEnabled, handleNavigate: handleNavigate }) }), _jsx("div", { id: "activity-panel", role: "tabpanel", hidden: activeTab !== FeatureOptions.activity, children: _jsx(ErrorBoundary, { fallback: _jsx(GenericError, {}), children: _jsx(TableContainer, { activeTransactionId: activeTransactionId, onSelectActivity: handleSelectActivity, selectPayEligibleTransactions: eligibleTransactions, disputesLinkEnabled: disputesLinkEnabled, handleNavigate: handleNavigate }) }) })] }) }), _jsx(RenderIf, { condition: showQuestionnaire, children: _jsx(SyfDisputes, { accountId: accountId, disputesMFEConfig: getDisputesConfig(config), feature: DisputeFeature === null || DisputeFeature === void 0 ? void 0 : DisputeFeature.Questionnaire, navigationHandler: handleNavigate, returnToParent: noop }) }), _jsx("div", { id: "mma-select-pay-modal-container" }), _jsx("div", { id: "mma-spending-trends-drawer-container" }), _jsx("div", { id: "mma-promo-details-modal-container" }), _jsx("div", { id: "mma-modal-container", children: _jsx(TransactionProvider, { value: {
                        currentTransaction: currentTransaction,
                        changeTab: handleTab,
                        closeModal: handleCloseModal,
                        disputesEnabled: disputesEnabled,
                        disputesLinkEnabled: disputesLinkEnabled,
                        changeModal: setShowQuestionnaire,
                        handleNavigate: handleNavigate
                    }, children: _jsx(Modal, { closeModal: handleCloseModal, currentModal: currentModal, open: isExtraInfoOpen, pendingPayment: shouldCancelPaymentRender, setCurrentModal: setCurrentModal, transactionStatus: currentTransaction === null || currentTransaction === void 0 ? void 0 : currentTransaction.status, disputesButtonEnabled: disputesFooterLinkEnabled, children: _jsxs(_Fragment, { children: [_jsx(RenderIf, { condition: Boolean(currentTransaction && currentModal === ModalOptions.default), children: _jsx(ExtraInfo, { accountId: accountId, config: config, currentTransaction: currentTransaction, type: currentTransaction === null || currentTransaction === void 0 ? void 0 : currentTransaction.type }) }), _jsx(RenderIf, { condition: currentModal === ModalOptions.cancelInitiated &&
                                        cancelPaymentFetching, children: _jsx(Inset, { all: "xlarge", children: _jsx(VerticalCardLoader, {}) }) }), _jsx(RenderIf, { condition: currentModal === ModalOptions.cancelError, children: _jsx(GenericError, { image: _jsx(CanceledErrorIllustration, {}), titleKey: "activityDetails.cancelPayment.canceledErrorHeader", subtitleKey: "activityDetails.cancelPayment.canceledErrorMessage" }) }), _jsx(RenderIf, { condition: currentModal === ModalOptions.cancelConfirm, children: _jsx(CancelPaymentConfirm, { currentTransaction: currentTransaction }) }), _jsx(RenderIf, { condition: currentModal === ModalOptions.cancelComplete, children: _jsx(CancelPaymentComplete, { currentTransaction: currentTransaction }) })] }) }) }) })] }));
};
export default Main;
//# sourceMappingURL=index.js.map